import { AuthenticatedGuard } from "@/components/authenticated-guard";
import Header from "@/components/header.tsx";
import { LocalOperatorShell } from "@/components/non_registrant/local-operator-shell";
import { PortalDashboardSubNav } from "@/components/portal/portal-dashboard-sub-nav";
import { PortalSectionSubNav } from "@/components/portal/portal-section-sub-nav";
import { PortalSubNav } from "@/components/portal/portal-sub-nav";
import { UserType } from "@/constants";
import { CommissionIdentityProvider } from "@/contexts/commission-identity-context";
import { getOperatorAccess } from "@/hera-api/operator-access-api";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useLicenseChangeEvents } from "@/hooks/use-license-change-events";
import { useTitleRequestChangeEvents } from "@/hooks/use-title-request-change-events";
import {
  localOperatorPreviewEnabled,
  operatorAuthConfigured,
} from "@/lib/local-operator-preview";
import {
  getOperatorFinancialsSubNavItems,
  getOperatorStrSubNavItems,
  isOperatorDashboardPath,
  isOperatorFinancialsPath,
  isOperatorStrPath,
  isPortalSetupPath,
} from "@/lib/portal-nav";
import { getUserType } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import Auth0Provider from "../integrations/auth0/root-provider.tsx";

function OperatorLayout() {
  const { user } = useAuth0();
  const userType = getUserType(user);
  const { pathname } = useLocation();
  const access = useOperatorAccess(user?.email);
  useLicenseChangeEvents();
  useTitleRequestChangeEvents();
  const showPortalSubNav =
    userType === UserType.OPERATOR && !isPortalSetupPath(pathname);

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <Header
        user={user || null}
        fullWidth={true}
        showOperatorHelp
        blogPath="/operator/blog"
      />
      {showPortalSubNav && <PortalSubNav userType={userType} />}
      {showPortalSubNav && isOperatorDashboardPath(pathname) ? (
        <PortalDashboardSubNav />
      ) : null}
      {showPortalSubNav && isOperatorFinancialsPath(pathname) ? (
        <PortalSectionSubNav
          label="Financials"
          items={getOperatorFinancialsSubNavItems()}
        />
      ) : null}
      {showPortalSubNav && isOperatorStrPath(pathname) ? (
        <PortalSectionSubNav label="STR" items={getOperatorStrSubNavItems()} />
      ) : null}
      <main className="flex flex-1 min-h-0 w-full overflow-hidden p-4 md:p-6 bg-background">
        <AuthenticatedGuard allowedUserType={UserType.OPERATOR}>
          <div
            data-slot="portal-page-scroll"
            className="flex flex-1 min-h-0 w-full min-w-0 flex-col overflow-y-auto"
          >
            <CommissionIdentityProvider
              email={access.email}
              isSales={access.isSales}
              ready={access.ready}
            >
              <Outlet />
            </CommissionIdentityProvider>
          </div>
        </AuthenticatedGuard>
      </main>
    </div>
  );
}

export const Route = createFileRoute("/operator")({
  component: OperatorEntry,
});

function useOperatorAccess(authEmail: string | undefined): {
  ready: boolean;
  isSales: boolean;
  email: string;
} {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const query = useQuery({
    queryKey: ["operator-access"],
    queryFn: getOperatorAccess,
    enabled: isAuthSetupComplete,
  });
  return {
    ready: isAuthSetupComplete && (query.isSuccess || query.isError),
    isSales: Boolean(query.data?.is_sales),
    email: (query.data?.email ?? authEmail ?? "").trim().toLowerCase(),
  };
}

function OperatorEntry() {
  if (
    localOperatorPreviewEnabled(import.meta.env.DEV, operatorAuthConfigured())
  ) {
    return <LocalOperatorShell />;
  }

  return (
    <Auth0Provider
      audience={import.meta.env.VITE_AUTH0_OPERATOR_AUDIENCE}
      clientId={import.meta.env.VITE_AUTH0_OPERATOR_CLIENT_ID}
      callbackPath="/operator/auth_callback"
    >
      <OperatorLayout />
    </Auth0Provider>
  );
}
